// Core BLS types
export type Direction = 'horizontal' | 'vertical' | 'diagonal' | 'infinity' | 'random';
export type SoundName = 'off' | 'tap' | 'beep' | 'heartbeat' | 'rain' | 'ocean' | 'snap' | 'wood' | 'bamboo' | 'drum' | 'tibetan_ping' | 'ambient' | 'angelic' | 'handpan' | 'underwater' | 'drone';
export type SubscriptionTier = 'free' | 'trial' | 'pro';
export type UserRole = 'host' | 'participant';

// New BLS feature types
export type MovementStyle = 'smooth' | 'spread' | 'blink' | 'pulse';
export type Shape = 'ball' | 'circle' | 'square' | 'triangle' | 'star';

// Mixed Mode Configuration
export interface MixedModeConfig {
  randomizeColor: boolean;
  randomizeDirection: boolean;
  randomizeMovementStyle: boolean;
  randomizeBackground: boolean;
  randomizeShape: boolean;
  randomizeSounds: boolean;
}

// BLS State
export interface BLSState {
  isPlaying: boolean;
  color: string;
  speed: number; // 1-15 (display levels), maps to 0.05-4.0 Hz internally
  volume: number; // 0-1
  selectedSounds: SoundName[]; // Array of selected sounds (single or multiple)
  direction: Direction;
  circleSize: number; // pixels (diameter)
  muteTherapistAudio: boolean; // Mute BLS audio for therapist only
  background: string; // Background color (from BACKGROUND_COLORS)
  movementStyle: MovementStyle;
  mixedMode: boolean; // Randomizes colors, sounds, and movement patterns
  mixedModeConfig: MixedModeConfig; // Configuration for which parameters to randomize
  shape: Shape;
  visualEnabled: boolean; // Toggle for visual (dot movement)
  audioEnabled: boolean; // Toggle for audio
  audioMultiSelect: boolean; // Single-select (false) or multi-select (true) mode
}

// BLS Actions
export type BLSAction =
  | { type: 'START' }
  | { type: 'STOP' }
  | { type: 'SET_COLOR'; color: string }
  | { type: 'SET_SPEED'; speed: number }
  | { type: 'SET_VOLUME'; volume: number }
  | { type: 'SET_SELECTED_SOUNDS'; sounds: SoundName[] }
  | { type: 'SET_DIRECTION'; direction: Direction }
  | { type: 'SET_CIRCLE_SIZE'; size: number }
  | { type: 'SET_MUTE_THERAPIST_AUDIO'; muted: boolean }
  | { type: 'SET_BACKGROUND'; background: string }
  | { type: 'SET_MOVEMENT_STYLE'; style: MovementStyle }
  | { type: 'SET_MIXED_MODE'; enabled: boolean }
  | { type: 'SET_MIXED_MODE_CONFIG'; config: Partial<MixedModeConfig> }
  | { type: 'TOGGLE_ALL_RANDOM'; enabled: boolean }
  | { type: 'SET_SHAPE'; shape: Shape }
  | { type: 'SET_VISUAL_ENABLED'; enabled: boolean }
  | { type: 'SET_AUDIO_ENABLED'; enabled: boolean }
  | { type: 'SET_AUDIO_MULTI_SELECT'; enabled: boolean }
  | { type: 'SET_STATE'; state: Partial<BLSState> };

// Freemium Configuration
export interface FreeTierLimits {
  monthlySessionLimit: number; // How many BLS sessions per calendar month
}

export interface SessionUsage {
  month: string; // "YYYY-MM"
  count: number;
}

export interface ProFeatures {
  colors: 'all';
  directions: Direction[];
  sounds: 'all';
  speedRange: [number, number];
  volumeControl: boolean;
  sessionDuration: null;
  circleSize: boolean;
  effects: boolean;
  presets: boolean;
  timer: boolean;
  backgrounds: 'all'; // All background colors allowed
  movementStyles: MovementStyle[];
  mixedMode: boolean;
  mixedModeConfig: MixedModeConfig; // All randomization parameters allowed
  shapes: Shape[];
}

export const FREE_LIMITS: FreeTierLimits = {
  monthlySessionLimit: 5,
};

export const PRO_FEATURES: ProFeatures = {
  colors: 'all',
  directions: ['horizontal', 'vertical', 'diagonal', 'infinity', 'random'],
  sounds: 'all',
  speedRange: [1, 15], // 1-15 display levels (0.05-4.0 Hz)
  volumeControl: true,
  sessionDuration: null,
  circleSize: true,
  effects: true,
  presets: true,
  timer: true,
  backgrounds: 'all', // All background colors allowed
  movementStyles: ['smooth', 'spread', 'blink', 'pulse'],
  mixedMode: true,
  mixedModeConfig: {
    randomizeColor: true,
    randomizeDirection: true,
    randomizeMovementStyle: true,
    randomizeBackground: true,
    randomizeShape: true,
    randomizeSounds: true,
  },
  shapes: ['ball', 'circle', 'square', 'triangle', 'star'],
};

// Zoom SDK types
export interface ZoomMessage {
  action: 'start_bls' | 'stop_bls' | 'client_stopped' | 'update_state' | 'request_state' | 'client_connected' | 'client_disconnected';
  params?: Partial<BLSState>;
  sender?: string; // ID of the message sender (for tracking client connections)
}

export interface MeetingContext {
  role: string;
  participantId: string;
  meetingId: string;
}

// Audio Engine types
export interface AudioConfig {
  sound: SoundName;
  speed: number;
  volume: number;
}

// Animation Engine types
export interface AnimationConfig {
  direction: Direction;
  speed: number;
  color: string;
  size: number; // Circle diameter in pixels
  movementStyle: MovementStyle;
}

export interface Position {
  x: number;
  y: number;
}

// Sound metadata
export interface SoundMetadata {
  displayName: string;
  description: string;
  icon: string; // Emoji icon
  isPro: boolean;
  simpleMode: boolean; // True if available in simple mode
  order: number; // Display order
}

export const SOUND_LIBRARY: Record<SoundName, SoundMetadata> = {
  // No sound option
  off: { displayName: 'Off', description: 'No sound', icon: '🔇', isPro: false, simpleMode: true, order: 0 },

  // Discrete beep sounds (triggered at extremes)
  tap: { displayName: 'Tap', description: 'Soft tap sound', icon: '👆', isPro: false, simpleMode: true, order: 1 },
  beep: { displayName: 'Beep', description: 'Simple beep tone', icon: '📢', isPro: false, simpleMode: true, order: 2 },
  heartbeat: { displayName: 'Heartbeat', description: 'Heartbeat rhythm', icon: '💓', isPro: false, simpleMode: true, order: 3 },
  rain: { displayName: 'Rain', description: 'Raindrop sound', icon: '💧', isPro: false, simpleMode: true, order: 4 },
  snap: { displayName: 'Snap', description: 'Finger snap', icon: '🫰', isPro: false, simpleMode: true, order: 5 },
  wood: { displayName: 'Wood', description: 'Wood knock', icon: '🪵', isPro: false, simpleMode: false, order: 6 },
  bamboo: { displayName: 'Bamboo', description: 'Bamboo hit', icon: '🎋', isPro: false, simpleMode: true, order: 7 },
  drum: { displayName: 'Drum', description: 'Drum hit', icon: '🥁', isPro: false, simpleMode: false, order: 8 },
  tibetan_ping: { displayName: 'Tibetan', description: 'Tibetan singing bowl', icon: '🔔', isPro: false, simpleMode: false, order: 9 },

  // Continuous looping sounds (smooth panning)
  ocean: { displayName: 'Ocean', description: 'Ocean waves loop', icon: '🌊', isPro: false, simpleMode: true, order: 10 },
  ambient: { displayName: 'Ambient', description: 'Calming ambient pads', icon: '✨', isPro: false, simpleMode: false, order: 11 },
  angelic: { displayName: 'Angelic', description: 'Ethereal pad texture', icon: '👼', isPro: false, simpleMode: false, order: 12 },
  handpan: { displayName: 'Handpan', description: 'Melodic meditation loop', icon: '🎵', isPro: false, simpleMode: false, order: 13 },
  underwater: { displayName: 'Underwater', description: 'Aquatic ambience loop', icon: '🐠', isPro: false, simpleMode: false, order: 14 },
  drone: { displayName: 'Drone', description: 'Textural pad drone', icon: '🎚️', isPro: false, simpleMode: false, order: 15 },
};

// Color presets for dot colors (therapeutic colors)
export interface ColorPreset {
  name: string;
  value: string;
  isPro: boolean;
}

export const COLOR_PRESETS: ColorPreset[] = [
  { name: 'Ocean Blue', value: '#1471ed', isPro: false },
  { name: 'Teal', value: '#14B8A6', isPro: false },
  { name: 'Rose', value: '#FB7185', isPro: false },
  { name: 'Lavender', value: '#9333EA', isPro: false },
  { name: 'Soft Pink', value: '#EC4899', isPro: false },
  { name: 'Sage Green', value: '#84CC96', isPro: false },
  { name: 'Sky Blue', value: '#7DD3FC', isPro: false },
  { name: 'Coral', value: '#F59E0B', isPro: false },
  { name: 'Indigo', value: '#6366F1', isPro: false },
  { name: 'Golden Yellow', value: '#FFD700', isPro: false },
  { name: 'White', value: '#f4f7f9', isPro: false },
];

// Background color presets (lighter, pastel therapeutic colors)
export const BACKGROUND_COLORS: ColorPreset[] = [
  { name: 'Soft Blue', value: '#DBEAFE', isPro: false },
  { name: 'White', value: '#FFFFFF', isPro: false },
  { name: 'Black', value: '#000000', isPro: false },
  { name: 'Warm Beige', value: '#F5F5DC', isPro: false },
  { name: 'Pale Green', value: '#D1FAE5', isPro: false },
  { name: 'Light Teal', value: '#CCFBF1', isPro: false },
  { name: 'Lavender Mist', value: '#EDE9FE', isPro: false },
];

// Audio manifest types (for caching)
export interface AudioManifestEntry {
  hash: string;   // SHA-256 hash of the audio file
  size: number;   // File size in bytes
  url: string;    // URL to fetch the file
}

export type AudioManifest = Record<SoundName, AudioManifestEntry>;
